import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SettingConfigurationForm } from './ArcgisCivil3dSettingConfigurationForm';
import { useTemplates } from '../../../hooks/api/use-templates.hook';
import { useCreateSettingConfiguration } from '../../../hooks/api/use-arcgis-civil3d-setting-configurations.hook';

export const AddSettingConfiguration = () => {
  const navigate = useNavigate();
  const [createSettingConfiguration, { loading: creatingSettingConfiguration, error: createError }] =
    useCreateSettingConfiguration();
  const { loading: loadingTemplates, response: templatesResponse } = useTemplates({});
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SettingConfigurationForm>({
    resolver: classValidatorResolver(SettingConfigurationForm),
  });

  const onSubmit: SubmitHandler<SettingConfigurationForm> = async (values) => {
    await createSettingConfiguration({ ...values, templateId: values.template?.id! });

    navigate('..');
  };

  const renderContent = () => (
    <div>
      <TextField
        {...register('name')}
        variant="outlined"
        sx={{ mt: '1rem' }}
        disabled={creatingSettingConfiguration}
        error={Boolean(errors.name)}
        helperText={errors.name?.message}
        label="Name"
        margin="dense"
        size="small"
        fullWidth
      />
      <Controller
        control={control}
        name="template"
        render={({ field }) => (
          <Autocomplete
            {...register('template')}
            loading={loadingTemplates}
            disabled={creatingSettingConfiguration}
            sx={{ mt: '1rem' }}
            onChange={(_event, selectedOptions) => field.onChange(selectedOptions)}
            onBlur={field.onBlur}
            value={field.value}
            getOptionLabel={(template) => template?.name ?? ''}
            options={templatesResponse?.pages.map(({ result }) => result).flat() ?? []}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Template"
                error={Boolean(errors.template)}
                helperText={errors.template?.message}
              />
            )}
          />
        )}
      />
    </div>
  );

  return (
    <Dialog open={true}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: 600 }}>
        <DialogTitle>Add ArcGIS Civil 3D Configuration</DialogTitle>
        <DialogContent>
          {renderContent()}
          {createError ? (
            <Alert sx={{ mt: '1rem' }} severity="error">
              {createError?.response?.data?.message}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
            Cancel
          </Button>
          <LoadingButton color="primary" type="submit" variant="contained" loading={isSubmitting}>
            Create
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
